import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { ProductCard } from "@/components/ProductCard";
import { BLOG } from "@/constants/testIds";
import { ArticleShare } from "@/components/ArticleShare";

const TAG = /^\[\[([a-z0-9-]+)\]\]$/;

const Block = ({ line, products }) => {
  const tag = line.match(TAG);
  if (tag) {
    const p = products[tag[1]];
    return p ? <div data-testid={BLOG.product(p.slug)} className="my-6 max-w-xs"><ProductCard product={p} /></div> : null;
  }
  if (line.startsWith("## ")) return <h2 className="font-heading text-2xl font-bold mt-10 mb-3">{line.slice(3)}</h2>;
  return <p className="text-stone-700 leading-relaxed my-4">{line}</p>;
};

const useArticleJsonLd = (post) => {
  useEffect(() => {
    if (!post) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "BlogPosting", headline: post.title, description: post.excerpt,
      image: post.image ? [imgUrl(post.image)] : undefined, datePublished: post.published_at, dateModified: post.updated_at,
      author: { "@type": "Organization", name: "PetTok Shop" }, publisher: { "@type": "Organization", name: "PetTok Shop", logo: { "@type": "ImageObject", url: "https://pettokshop.net/icons/icon-512.png" } },
      mainEntityOfPage: `https://pettokshop.net/blog/${post.slug}`,
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [post]);
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setPost(null);
    setMissing(false);
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setMissing(true));
  }, [slug]);
  useSeo({ title: post ? `${post.title} | PetTok Shop` : "Kiddee's toy tests | PetTok Shop", description: post?.excerpt, path: `/blog/${slug}`, image: post?.image });
  useArticleJsonLd(post);
  if (missing) return <main className="pt-32 pb-16 max-w-3xl mx-auto px-6"><p>That article isn't available. <Link to="/blog" className="text-[#E06D44] underline">See all toy tests</Link></p></main>;
  if (!post) return <main className="pt-32 pb-16 max-w-3xl mx-auto px-6"><p className="text-stone-500">Loading…</p></main>;
  const products = Object.fromEntries(post.product_items.map((p) => [p.slug, p]));
  return (
    <main data-testid={BLOG.post} className="pt-24 sm:pt-32 pb-16 max-w-3xl mx-auto px-5 sm:px-6">
      <Link to="/blog" data-testid={BLOG.back} className="text-sm text-stone-500 hover:text-[#E06D44]">← All toy tests</Link>
      <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-4 leading-tight">{post.title}</h1>
      <p className="mt-4 text-lg text-stone-600">{post.excerpt}</p>
      <div className="mt-5"><ArticleShare testId="blog-share-top" title={post.title} text={post.excerpt} /></div>
      {post.image && <img src={imgUrl(post.image)} alt={post.title} className="mt-8 w-full aspect-[16/9] object-cover rounded-3xl" />}
      <article className="mt-6">{post.body.split(/\n+/).map((l) => l.trim()).filter(Boolean).map((l, i) => <Block key={i} line={l} products={products} />)}</article>
      <div data-testid="blog-share-footer" className="mt-10 rounded-3xl bg-[#FDF2EC] p-6 flex flex-wrap items-center justify-between gap-4">
        <p className="font-heading font-bold text-lg">Know a pup who'd love these?</p>
        <ArticleShare testId="blog-share-bottom" title={post.title} text={post.excerpt} label="Share this toy test" />
      </div>
    </main>
  );
}
